import React, { useState } from "react"
import { Modal } from "react-bootstrap"
import { AddFromOrderModalProps } from "../../common/types/inventory"
import { AddButton, CancelButton } from "../../common/button"
import { ADD_FROM_ORDER_MODAL_MESSAGES, STOCK_DOCUMENTS } from "../../utils/inventory"
import { injectIntl } from "react-intl"
import UseListingPagination from "../../common/hooks/UseListingPagination"
import { generateOrderLineSearchParams } from "../../utils"
import { useQuery } from "react-query"
import SearchService from "../../state/search/SearchService"
import { OrderLineElastic } from "../../state/order/models"
import StandardList from "../../common/listing/list.component"
import { AddProductsFromOpenOrdersHeaders } from "../../utils/inventory/utils"
import Checkbox from "../../common/checkbox/checkbox.component"
import LoadingSpinner from "../../common/spinner/loading.component"
import NoResult from "../../common/listing/noresult.component"
import { useDispatch, useSelector } from "react-redux"
import { addMultiItemsFromReference } from "../../state/pricing/actions"
import { PricingTableSelector } from "../../state/pricing/selectors"
import { ORDER_LINES, ORDERS } from "../../state/order/Constants"
import Search from "../../common/search-filter/search/search.component"
import GlobalHelper from "../../common/GlobalHelper"

export const AddFromOrderModal = injectIntl((props: AddFromOrderModalProps) => {
    const referenceIds = useSelector(PricingTableSelector.getReferenceRowIDs)
    const [selected, setSelected] = useState<Array<OrderLineElastic>>([])
    const dispatch = useDispatch()
    const [keyword, setKeyword] = useState<string>("")

    const {
        pagination,
        updateTotalItemsCount,
        paginatedSearch
    } = UseListingPagination(generateOrderLineSearchParams())

    const queryKey = ["add-from-order-listing", paginatedSearch, keyword]
    const {data, isLoading, isFetched} = useQuery(queryKey, () => {
        return SearchService.mainSearch<OrderLineElastic>({
            ...generateOrderLineSearchParams({
                ...paginatedSearch,
                keywords: keyword,
                filters: {
                    "locationId": props.mainLocationIds,
                    "inventoryTracking": true,
                    "inventoryRequestedMeasure": ">;0",
                    "inventoryRequestedWithPo": "0",
                    "rowId{notOneOf};": referenceIds
                }
            }),
        })
    }, {
        onSuccess: (data) => {
            setSelected([])
            updateTotalItemsCount(data.metadata.totalItemsCount)
        }
    })

    const headers = [
        ...AddProductsFromOpenOrdersHeaders,
        <Checkbox
            key={"check-all"}
            checked={selected.length === data?.data?.length}
            onChange={() => {
                setSelected(
                    selected.length === data?.data?.length
                        ? []
                        : data?.data ?? [])
            }}
        />
    ]

    const rows = data?.data?.map((item) => {
        return [
            item.productTitle,
            item.orderIdentifier,
            item.contact.name,
            item.inventoryRequestedMeasure,
            <Checkbox
                key={"check-single"}
                checked={selected.findIndex((i) => i.id === item.id) !== -1}
                onChange={() => {
                    setSelected(current =>
                        current.find(i => item.id === i.id) ?
                            current.filter((i) => i.id !== item.id)
                            : [...current, item]
                    )
                }}
            />
        ]
    })

    const getListing = () => {
        if (isLoading) {
            return <LoadingSpinner/>
        }

        if (isFetched && data?.data?.length === 0) {
            return <NoResult
                noButton={true}
            />
        }

        return (
            <>
                <StandardList
                    className={"px-0"}
                    headers={headers}
                    rows={rows}
                />

                <div className={"mt-2"}>
                    {pagination()}
                </div>
            </>
        )
    }

    const handleChange = (_savePrefs, _refreshTotal, _refreshStatBox, data) => {
        setKeyword(GlobalHelper.displaySearchKeywords(data.keywords))
    }
    const onAdd = () => {
        dispatch(addMultiItemsFromReference(
            selected.map(item => ({
                references: [
                    {
                        referenceId: item.id,
                        referenceIdentifier: item.orderIdentifier,
                        referenceKey: ORDER_LINES,
                    },
                    {
                        referenceId: item.orderId,
                        referenceIdentifier: item.orderIdentifier,
                        referenceKey: ORDERS,
                    },
                ],
                supplierId: props.supplierId,
                measure: item.inventoryRequestedMeasure,
                productId: item.productId,
            }))
        ))
        props.onCloseCallback()
    }

    return (
        <Modal
            show={true}
            onHide={props.onCloseCallback}
            size={"lg"}
            backdrop="static"
        >
            <Modal.Header closeButton>
                <Modal.Title>
                    {props.intl.formatMessage(ADD_FROM_ORDER_MODAL_MESSAGES.header)}
                </Modal.Title>
            </Modal.Header>

            <Modal.Body>
                <Search
                    onChange={handleChange}
                    key={STOCK_DOCUMENTS}
                    typeOfSearch={STOCK_DOCUMENTS}
                    object={[STOCK_DOCUMENTS]}
                    skipInitialChangeEvent={true}
                    searchKeywords={keyword}
                    filtersToUse={paginatedSearch.filters}
                    searchBarClass={"col-md-7"}
                    placeholder={props.intl.formatMessage(ADD_FROM_ORDER_MODAL_MESSAGES.search_placeholder)}
                    hideFilters={true}
                    hideFilterDropdown={true}
                />
                {getListing()}
            </Modal.Body>

            <Modal.Footer>
                <CancelButton onClickCallback={props.onCloseCallback}/>

                <AddButton onClickCallback={onAdd} disabled={selected.length === 0}/>
            </Modal.Footer>
        </Modal>
    )
})


// WEBPACK FOOTER //
// ./src/inventory/modals/AddFromOrderModal.tsx